/**
 * @file pages/Users.jsx
 * @description User management page (admin only).
 *
 * Features:
 *  - Lists all local application users with their role and active status
 *  - Inline role selector (dropdown)
 *  - Toggle active/inactive
 *  - Deactivate button (soft-delete)
 *
 * Self-modification is blocked on the backend; a visual hint is shown to the
 * admin viewing their own row.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import DataTable from '../components/DataTable';
import { listUsers, updateUser, deactivateUser } from '../api/users';
import apiClient from '../api/client';

/** Available application roles. */
const ROLES = ['admin', 'operator', 'viewer'];

/**
 * Returns a Bootstrap badge variant name for a given role string.
 * @param {string} role
 * @returns {string} Bootstrap colour variant.
 */
function roleBadgeStyle(role) {
  const map = {
    admin:    { bg: 'var(--color-primary)',  color: '#fff' },
    operator: { bg: 'var(--color-accent-2)', color: '#000' },
    viewer:   { bg: 'var(--color-accent-3)', color: '#000' },
  };
  return map[role] || { bg: '#6c757d', color: '#fff' };
}

/**
 * User management page component.
 */
export default function Users() {
  const queryClient = useQueryClient();

  // Fetch current user to know whose row is "self" (to prevent self-deactivation in UI)
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn:  async () => (await apiClient.get('/api/users/me')).data,
    staleTime: 10 * 60 * 1000,
  });

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn:  listUsers,
  });

  // Update mutation — used for both role changes and active toggling
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateUser(id, payload),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivateUser,
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const handleRoleChange = (user, role) => {
    updateMutation.mutate({ id: user.id, payload: { role } });
  };

  const handleDeactivate = (user) => {
    if (window.confirm(`Deactivate "${user.name}"? They will lose access immediately.`)) {
      deactivateMutation.mutate(user.id);
    }
  };

  /** DataTable column definitions */
  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <div>
          <strong>{row.name}</strong>
          {currentUser?.id === row.id && (
            <span className="badge bg-secondary ms-2 small">You</span>
          )}
          <div className="text-muted small">{row.email}</div>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (row) => {
        const isSelf = currentUser?.id === row.id;
        return (
          <select
            className="form-select form-select-sm w-auto"
            value={row.role}
            onChange={(e) => handleRoleChange(row, e.target.value)}
            disabled={isSelf || updateMutation.isPending}
            aria-label={`Role for ${row.name}`}
            title={isSelf ? 'You cannot change your own role' : undefined}
          >
            {ROLES.map((r) => {
              const { bg, color } = roleBadgeStyle(r);
              return (
                <option key={r} value={r} style={{ backgroundColor: bg, color }}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              );
            })}
          </select>
        );
      },
    },
    {
      key: 'active',
      label: 'Active',
      render: (row) => (
        row.active
          ? <span className="badge" style={{ backgroundColor: 'var(--color-accent-2)', color: '#000' }}>Active</span>
          : <span className="badge bg-secondary">Inactive</span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Joined',
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => {
        const isSelf = currentUser?.id === row.id;
        if (!row.active) return <span className="text-muted small">Deactivated</span>;
        return (
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDeactivate(row)}
            disabled={isSelf || deactivateMutation.isPending}
            title={isSelf ? 'You cannot deactivate your own account' : undefined}
            aria-label={`Deactivate ${row.name}`}
          >
            <i className="bi bi-person-x me-1" aria-hidden="true" />
            Deactivate
          </button>
        );
      },
    },
  ];

  return (
    <div className="page-content">
      <h1 className="h4 fw-bold mb-4" style={{ color: 'var(--color-primary)' }}>
        <i className="bi bi-people-fill me-2" aria-hidden="true" />
        Users
      </h1>

      {/* Mutation errors */}
      {(updateMutation.isError || deactivateMutation.isError) && (
        <div className="alert alert-danger mb-3" role="alert">
          {updateMutation.error?.message || deactivateMutation.error?.message || 'Operation failed.'}
        </div>
      )}

      <div className="card">
        <div className="card-body p-0">
          <DataTable
            columns={columns}
            data={users}
            isLoading={isLoading}
            error={error?.message}
            emptyMessage="No users found."
            getRowKey={(row) => row.id}
          />
        </div>
      </div>

      <p className="text-muted small mt-3">
        <i className="bi bi-info-circle me-1" aria-hidden="true" />
        Users are auto-provisioned on first login with the <strong>viewer</strong> role.
        Promote them here after verifying their identity.
      </p>
    </div>
  );
}
